import React, { useMemo, useState } from 'react';
import { LifeScoreInfo } from '../components/LifeScoreInfo';
import { LifeScoreRing } from '../components/LifeScoreRing';
import { MetricCard } from '../components/MetricCard';
import { FilterChip } from '../components/FilterChip';
import { PageHeader } from '../components/PageHeader';
import { Card, SectionTitle } from '../components/ui/Card';
import { currentUser } from '../data/user';
import { useLocale } from '../contexts/LocaleContext';
import { useProgress } from '../contexts/ProgressContext';
import { average, buildHistory, perfectDays } from '../utils/history';
import { bandForScore, bandKey } from '../utils/progress';
type Period = 'today' | 'week' | 'month' | 'year';
const periods: Period[] = ['today', 'week', 'month', 'year'];
export function LifeScoreDetail() {
  const {
    t,
    dayUnit
  } = useLocale();
  const progress = useProgress();
  const [period, setPeriod] = useState<Period>('today');
  const history = useMemo(() => buildHistory(90, currentUser.streak), []);
  const withToday = useMemo(() => [...history, {
    date: new Date(),
    score: progress.todayScore
  }], [history, progress.todayScore]);
  const windows: Record<Period, typeof withToday> = {
    today: withToday.slice(-1),
    week: withToday.slice(-7),
    month: withToday.slice(-30),
    year: withToday
  };
  const slice = windows[period];
  const score = period === 'today' ? progress.todayScore : average(slice);
  const best = Math.max(...slice.map(day => day.score));
  const strongDays = slice.filter(day => day.score >= 70).length;
  const missed = slice.filter(day => day.score === 0).length;
  return <div className="px-2 pb-2" data-id="element-3149" data-mp="5a31gnfx04i7">
      <PageHeader title={t('lifescore.title')} subtitle={t('lifescore.subtitle')} showBack action={<LifeScoreInfo data-id="element-3151" data-mp="1go6tho2xzikm" />} data-id="element-3150" data-mp="5773x07zbhua" />

      <Card className="p-2" data-id="element-3152" data-mp="1gndwhetsc7fo">
        <ul className="flex gap-1 rounded-pill bg-sand-deep/70 p-[3px]" role="tablist" data-id="element-3153" data-mp="1gmm0w406u9pe">
          {periods.map(item => <li key={item} className="flex-1" data-id="element-3154" data-mp="1jq1wt18usvz1">
              <button type="button" role="tab" aria-selected={period === item} onClick={() => setPeriod(item)} className={`min-h-[36px] w-full whitespace-nowrap rounded-pill px-1 text-[12px] font-medium transition-colors duration-150 ease-brand ${period === item ? 'bg-deep text-sand' : 'text-muted hover:text-deep'}`} data-id="element-3155" data-mp="1jp9y3hjymi4j">
                {t(`stats.period.${item}`)}
              </button>
            </li>)}
        </ul>

        <div className="flex flex-col items-center pt-3" data-id="element-3156" data-mp="1a0icrs9emw2l">
          <LifeScoreRing score={score} size={190} tone="light" bandLabel={t(bandKey(bandForScore(score)))} data-id="element-3157" data-mp="19yxg9x3j5323" />
          <p className="pb-1 pt-2 text-center text-[12px] text-muted" data-id="element-3158" data-mp="hajhvg8j44q0">
            {t(`stats.lifescore.${period}`)}
          </p>
        </div>
      </Card>

      <section className="mt-2 grid grid-cols-2 gap-2" data-id="element-3159" data-mp="m8b9sa96bqu7">
        <MetricCard label={t('lifescore.best')} value={best} unit="/100" hint={t('lifescore.bestHint')} data-id="element-3160" data-mp="eptxjw9wxxxj" />
        <MetricCard label={t('lifescore.strongDays')} value={strongDays} unit={dayUnit(strongDays)} hint={t('lifescore.strongHint')} accent data-id="element-3161" data-mp="4sp6pj54heae" />
        <MetricCard label={t('stats.perfect')} value={perfectDays(slice)} unit={dayUnit(perfectDays(slice))} data-id="element-3162" data-mp="3rxetbphy9spf" />
        <MetricCard label={t('lifescore.missed')} value={missed} unit={dayUnit(missed)} hint={t('lifescore.missedHint')} data-id="element-3163" data-mp="3n5dlcfrteo57" />
      </section>

      <Card className="mt-2 p-2" data-id="element-3164" data-mp="3d9tui7s3yfnb">
        <SectionTitle data-id="element-3165" data-mp="3d6o32lw0b1of">{t('lifescore.bands')}</SectionTitle>
        <ul className="space-y-1" data-id="element-3166" data-mp="388mk00ykz2cy">
          {([{
          key: 'band.strong',
          range: '70 – 100'
        }, {
          key: 'band.attention',
          range: '40 – 69'
        }, {
          key: 'band.low',
          range: '0 – 39'
        }] as const).map(band => <li key={band.key} className="flex items-center justify-between gap-2 border-b border-sand-line pb-1 last:border-b-0" data-id="element-3167" data-mp="1sp0prodv1na">
              <span className="text-[13px] font-medium text-deep" data-id="element-3168" data-mp="3o809wwz4saiz">
                {t(band.key)}
              </span>
              <span className="bee-num text-[12px] text-muted" data-id="element-3169" data-mp="3a7pklydgz3zx">{band.range}</span>
            </li>)}
        </ul>
        <p className="mt-2 text-[11px] leading-snug text-muted" data-id="element-3170" data-mp="3f8d664ixismr">
          {t('lifescore.note')}
        </p>
      </Card>
    </div>;
}